// Copyright (c) 2024-2026 Echonum LLC. All rights reserved.
// Product: Exploit Score ETS (Exploit Threat Score)
//
// This file is part of Exploit Score ETS, proprietary software developed by
// Echonum LLC. Unauthorized copying, distribution, modification, or use of
// this file, via any medium, is strictly prohibited without express written
// permission from Echonum LLC.
//
// Confidential and Proprietary.

import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import api from '../api/client'
import toast from 'react-hot-toast'
import { ArrowLeft, Loader2, Send, ArrowUpRight, Building2, User } from 'lucide-react'

interface Comment { id: string; author_type: string; author_name: string | null; content: string; created_at: string | null }
interface Detail {
  id: string; ticket_number: string; subject: string; description: string
  category: string; priority: string; status: string; escalated: boolean
  created_at: string | null; updated_at: string | null
  customer: { email: string | null; company_name: string | null; contact_name: string | null }
  comments: Comment[]
}
const fmt = (d: string | null) => (d ? new Date(d).toLocaleString() : '—')
const label = (s: string) => s.replace(/_/g, ' ')

export default function MSPTicketDetail() {
  const { ticketId } = useParams()
  const navigate = useNavigate()
  const [detail, setDetail] = useState<Detail | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [reply, setReply] = useState('')
  const [sending, setSending] = useState(false)
  const [escalateOpen, setEscalateOpen] = useState(false)
  const [escalateNote, setEscalateNote] = useState('')

  const load = async () => {
    try {
      const res = await api.get(`/api/portal/msp/support/tickets/${ticketId}`)
      setDetail(res.data)
    } catch (e: any) {
      if (e?.response?.status === 404) setNotFound(true)
      else console.error(e)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { load() }, [ticketId])

  const sendReply = async () => {
    if (!reply.trim()) return
    setSending(true)
    try {
      await api.post(`/api/portal/msp/support/tickets/${ticketId}/comments`, { content: reply })
      setReply('')
      await load()
      toast.success('Reply sent to customer')
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to send reply')
    } finally {
      setSending(false)
    }
  }

  const escalate = async () => {
    try {
      await api.post(`/api/portal/msp/support/tickets/${ticketId}/escalate`, { note: escalateNote || undefined })
      setEscalateOpen(false); setEscalateNote('')
      await load()
      toast.success('Escalated to Echonum')
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to escalate')
    }
  }

  if (loading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary-500" /></div>
  if (notFound || !detail) {
    return (
      <div className="space-y-4">
        <button onClick={() => navigate('/msp/support')} className="flex items-center gap-1 text-sm text-gray-400 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back to tickets</button>
        <div className="rounded-lg border border-dark-border bg-dark-card p-8 text-center text-gray-400">Ticket not found or not one of your customers'.</div>
      </div>
    )
  }

  const closed = detail.status === 'closed'
  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <button onClick={() => navigate('/msp/support')} className="flex items-center gap-1 text-sm text-gray-400 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back to tickets</button>

      <div className="rounded-lg border border-dark-border bg-dark-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold">{detail.subject}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
              <span className="font-mono">{detail.ticket_number}</span>
              <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{detail.customer.company_name || detail.customer.email}</span>
              <span className="capitalize">{detail.priority} priority</span>
              <span className="capitalize">· {label(detail.status)}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {detail.escalated
              ? <span className="flex items-center gap-1 rounded bg-purple-500/15 px-2 py-1 text-xs text-purple-300"><ArrowUpRight className="h-3.5 w-3.5" /> Escalated</span>
              : <button onClick={() => setEscalateOpen(v => !v)} className="flex items-center gap-1 rounded-lg border border-purple-500/40 bg-purple-500/10 px-3 py-1.5 text-xs text-purple-300 hover:bg-purple-500/20"><ArrowUpRight className="h-3.5 w-3.5" /> Escalate to Echonum</button>}
          </div>
        </div>

        {escalateOpen && (
          <div className="mt-3 rounded-lg border border-purple-500/30 bg-purple-500/5 p-3">
            <textarea value={escalateNote} onChange={e => setEscalateNote(e.target.value)} rows={2}
              placeholder="Optional note for Echonum (only Echonum sees this)…"
              className="w-full rounded border border-dark-border bg-gray-900/40 p-2 text-sm text-gray-200 placeholder-gray-600" />
            <div className="mt-2 flex justify-end gap-2">
              <button onClick={() => setEscalateOpen(false)} className="rounded px-3 py-1 text-sm text-gray-400">Cancel</button>
              <button onClick={escalate} className="rounded-lg bg-purple-600 px-3 py-1 text-sm text-white hover:bg-purple-700">Escalate</button>
            </div>
          </div>
        )}

        <p className="mt-4 whitespace-pre-wrap border-t border-dark-border pt-4 text-sm text-gray-300">{detail.description}</p>
      </div>

      {/* Thread */}
      <div className="space-y-3">
        {detail.comments.map((c) => {
          const isCustomer = c.author_type === 'customer'
          return (
            <div key={c.id} className={`flex gap-3 ${isCustomer ? '' : 'flex-row-reverse'}`}>
              <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${isCustomer ? 'bg-gray-700' : 'bg-primary-500/20'}`}>
                <User className={`h-4 w-4 ${isCustomer ? 'text-gray-300' : 'text-primary-400'}`} />
              </div>
              <div className={`max-w-[80%] rounded-lg p-3 ${isCustomer ? 'bg-dark-card' : 'bg-primary-500/10'}`}>
                <div className="mb-1 flex items-center gap-2 text-xs text-gray-500">
                  <span className="font-medium text-gray-300">{c.author_name || (isCustomer ? 'Customer' : 'You')}</span>
                  <span>· {fmt(c.created_at)}</span>
                </div>
                <p className="whitespace-pre-wrap text-sm text-gray-200">{c.content}</p>
              </div>
            </div>
          )
        })}
      </div>

      {/* Reply */}
      {closed ? (
        <div className="rounded-lg border border-dark-border bg-dark-card p-4 text-center text-sm text-gray-500">This ticket is closed.</div>
      ) : (
        <div className="rounded-lg border border-dark-border bg-dark-card p-4">
          <textarea value={reply} onChange={e => setReply(e.target.value)} rows={3}
            placeholder="Reply to your customer…"
            className="w-full rounded border border-dark-border bg-gray-900/40 p-2 text-sm text-gray-200 placeholder-gray-600" />
          <div className="mt-2 flex justify-end">
            <button onClick={sendReply} disabled={sending || !reply.trim()}
              className="flex items-center gap-1 rounded-lg bg-primary-500 px-4 py-2 text-sm text-white hover:bg-primary-600 disabled:opacity-50">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send reply
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
